import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  generateFixPreviews,
  getFixPreviews,
  updateFixPreview,
  applyFixes,
  getFixHistory,
  revertFix,
  reapplyFix,
} from '../services/api';
import type {
  GeneratePreviewsResponse,
  GetPreviewsResponse,
  UpdatePreviewRequest,
  UpdatePreviewResponse,
  ApplyFixesResponse,
  FixHistoryDto,
  RevertFixResponse,
  ReapplyFixResponse,
} from '../types';

/**
 * T062: Auto-fix hooks — TanStack React Query wrappers.
 *
 * @see contracts/autofix.md
 * @module
 */

// ─── Shopify Toast Helper ────────────────────────────────────

function showToast(message: string, isError = false): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const shopify = (globalThis as any).shopify;
  if (typeof shopify?.toast?.show === 'function') {
    shopify.toast.show(message, { isError });
  }
}

function toastError(err: Error): void {
  const msg = err.message || 'Something went wrong';
  showToast(msg, true);
}

// ─── Hooks ───────────────────────────────────────────────────

/** Fetch existing previews for a product. */
export function useFixPreviews(productId: string, status?: string) {
  return useQuery<GetPreviewsResponse>({
    queryKey: ['fix-previews', productId, status],
    queryFn: () => getFixPreviews(productId),
    enabled: !!productId,
    staleTime: 30_000,
  });
}

/** Generate new auto-fix previews. */
export function useGeneratePreviews() {
  const queryClient = useQueryClient();

  return useMutation<GeneratePreviewsResponse, Error, { productId: string }>({
    mutationFn: ({ productId }) => generateFixPreviews(productId),
    onSuccess: (data, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ['fix-previews', productId] });
      showToast(`Generated ${data.generatedCount} fix suggestion${data.generatedCount === 1 ? '' : 's'}`);
    },
    onError: toastError,
  });
}

/** Approve or reject a single preview. */
export function useUpdatePreview() {
  const queryClient = useQueryClient();

  return useMutation<
    UpdatePreviewResponse,
    Error,
    { previewId: string; productId: string } & UpdatePreviewRequest
  >({
    mutationFn: ({ previewId, status, merchantEdit }) =>
      updateFixPreview(previewId, { status, merchantEdit }),
    onSuccess: (_data, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ['fix-previews', productId] });
    },
    onError: toastError,
  });
}

/** Apply approved fixes to Shopify. */
export function useApplyFixes() {
  const queryClient = useQueryClient();

  return useMutation<ApplyFixesResponse, Error, { productId: string }>({
    mutationFn: ({ productId }) => applyFixes({ productId }),
    onSuccess: (data, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ['fix-previews', productId] });
      queryClient.invalidateQueries({ queryKey: ['fix-history', productId] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      showToast(`Applied ${data.appliedCount} fix${data.appliedCount === 1 ? '' : 'es'} to Shopify`);
    },
    onError: toastError,
  });
}

/** Fetch fix history for a product. */
export function useFixHistory(productId: string) {
  return useQuery<FixHistoryDto[]>({
    queryKey: ['fix-history', productId],
    queryFn: () => getFixHistory(productId),
    enabled: !!productId,
    staleTime: 60_000,
  });
}

/** Revert a previously applied fix. */
export function useRevertFix() {
  const queryClient = useQueryClient();

  return useMutation<RevertFixResponse, Error, { historyId: string; productId: string; force?: boolean }>({
    mutationFn: ({ historyId, force }) => revertFix({ historyId, force }),
    onSuccess: (data, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ['fix-history', productId] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      showToast(`Reverted "${data.field}" to original value`);
    },
    // Note: onError for revert is handled per-call in ProductDetailPage (conflict → force dialog)
  });
}

/** Re-apply a previously reverted fix. */
export function useReapplyFix() {
  const queryClient = useQueryClient();

  return useMutation<ReapplyFixResponse, Error, { historyId: string; productId: string }>({
    mutationFn: ({ historyId }) => reapplyFix({ historyId }),
    onSuccess: (data, { productId }) => {
      queryClient.invalidateQueries({ queryKey: ['fix-history', productId] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      showToast(`Re-applied fix for "${data.field}"`);
    },
    onError: toastError,
  });
}
